import type { InferOutput } from 'valibot'
import { array, object, optional, parse, string } from 'valibot'

const favoritesSchema = object({
  literature: optional(array(string()), []),
  law: optional(array(string()), []),
  journals: optional(array(string()), []),
  templates: optional(array(string()), []),
})

export type UserFavorites = InferOutput<typeof favoritesSchema>

export function useFavorites() {
  const { loggedIn: isFavoriteEnabled } = useUserSession()
  const personalization = usePersonalization('favorites')

  const favorites = computed<UserFavorites>(() => {
    const data = personalization.data.value

    try {
      return parse(favoritesSchema, data ?? {})
    }
    catch (error) {
      console.warn('Failed to parse favorites data, using default:', error)
      return { literature: [], law: [], journals: [], templates: [] }
    }
  })

  const literatureFavorites = computed(() => favorites.value.literature)
  const lawFavorites = computed(() => favorites.value.law)
  const journalsFavorites = computed(() => favorites.value.journals)
  const templatesFavorites = computed(() => favorites.value.templates)
  const isLoading = computed(() => personalization.isLoading.value)

  async function deleteFavoritesMetadata() {
    try {
      console.warn('[DEV] Deleting favorites metadata...')
      personalization.del()
    }
    catch (error) {
      console.error('[DEV] Error deleting favorites metadata:', error)
    }
  }

  function isFavorite(category: 'law' | 'literature' | 'journals' | 'templates', id: string): boolean {
    return favorites.value[category].includes(id)
  }

  function toggleFavorite(category: 'law' | 'literature' | 'journals' | 'templates', id: string) {
    // Only undefined blocks the write. A 404 reads as null and a failed refetch keeps the last list, and both are safe to write from.
    if (!isFavoriteEnabled.value || personalization.data.value === undefined) {
      return
    }

    const categoryItems = favorites.value[category]
    const index = categoryItems.indexOf(id)

    const newFavorites = {
      ...favorites.value,
      [category]: index === -1
        ? [...categoryItems, id]
        : categoryItems.filter(item => item !== id),
    }

    personalization.set(newFavorites)
  }

  return {
    literatureFavorites,
    lawFavorites,
    templatesFavorites,
    journalsFavorites,
    isFavoriteEnabled,
    isLoading,
    isFavorite,
    toggleFavorite,
    deleteFavoritesMetadata,
  }
}
